if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.initFiltering();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initFiltering() {
        if (typeof subscribe !== 'undefined') {
          this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId) return;
            this.filterMedia(event.data.variant);
          });
        }

        // Initial filter
        const initialVariantId = this.dataset.initialVariantId;
        const productJson = document.getElementById(`ProductJSON-${this.dataset.sectionId}`);
        if (productJson) {
          const product = JSON.parse(productJson.textContent);
          let selectedVariant = product.variants.find(v => v.id == initialVariantId);
          if (!selectedVariant) selectedVariant = product.variants[0];
          
          if (selectedVariant) {
            if (selectedVariant.featured_media) {
              this.setActiveMedia(`${this.dataset.sectionId}-${selectedVariant.featured_media.id}`, true);
            } else {
              this.filterMedia(selectedVariant);
            }
          }
        }
      }

      filterMedia(variant) {
        if (!variant || !variant.options) return;

        const productJson = document.getElementById(`ProductJSON-${this.dataset.sectionId}`);
        if (!productJson) return;

        const product = JSON.parse(productJson.textContent);
        const colorOptionIndex = product.options.findIndex(opt => opt.toLowerCase() === 'color' || opt.toLowerCase() === 'colour');
        
        if (colorOptionIndex === -1) return;

        const selectedColor = variant.options[colorOptionIndex];
        if (!selectedColor) return;

        const normalizedColor = selectedColor.toLowerCase().replace(/\s+/g, '-');
        const items = this.querySelectorAll('.product__media-item, .thumbnail-list__item');
        
        items.forEach(item => {
          const img = item.querySelector('img');
          const alt = img ? img.getAttribute('alt') : '';
          
          if (alt && alt.toLowerCase().includes('#color_')) {
            if (alt.toLowerCase().includes(`#color_${normalizedColor}`)) {
              item.classList.remove('hidden');
              item.style.display = '';
            } else {
              item.classList.add('hidden');
              item.style.display = 'none';
            }
          } else {
            // No color tag, show by default
            item.classList.remove('hidden');
            item.style.display = '';
          }
        });

        // Reset sliders if they exist
        if (this.elements.viewer && this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
        if (this.elements.thumbnails && this.elements.thumbnails.slider) {
          this.elements.thumbnails.resetPages();
        }
      }

      disconnectedCallback() {
        if (this.onVariantChangeUnsubscriber) {
          this.onVariantChangeUnsubscriber();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (prepend) {
          const productJson = document.getElementById(`ProductJSON-${this.dataset.sectionId}`);
          if (productJson) {
            const product = JSON.parse(productJson.textContent);
            const variant = product.variants.find(v => v.featured_media && `${this.dataset.sectionId}-${v.featured_media.id}` === mediaId);
            if (variant) this.filterMedia(variant);
          }
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.hidden)');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
